import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import rehypeHighlight from "rehype-highlight";
import "katex/dist/katex.min.css";
import "highlight.js/styles/github-dark.css";

const REMARK_PLUGINS = [remarkGfm, remarkMath];
const REHYPE_PLUGINS = [rehypeKatex, rehypeHighlight];

const MD_COMPONENTS = {
  a: ({ node, ...props }) => (
    <a {...props} target="_blank" rel="noopener noreferrer" />
  ),
  pre: ({ node, ...props }) => (
    <pre {...props} className="overflow-x-auto" />
  ),
};

/**
 * Renders AI markdown content with GFM tables, math (KaTeX), and code highlighting.
 * Styled to fit inside chat bubbles for both user and assistant roles.
 */
export const MarkdownMessage = ({ content, isUser = false }) => {
  const proseColor = isUser
    ? "prose-invert prose-p:text-white prose-strong:text-white prose-headings:text-white prose-code:text-white prose-a:text-white"
    : "prose-zinc dark:prose-invert";

  // Plugin/component arrays are module-level constants → no per-render allocations.
  // (useMemo would also work; constants are even cheaper.)
  const className = useMemo(() => (
    `prose prose-sm max-w-none break-words ${proseColor}
        prose-p:my-1 prose-p:leading-relaxed
        prose-headings:font-heading prose-headings:font-semibold prose-headings:mt-3 prose-headings:mb-1
        prose-ul:my-1 prose-ol:my-1 prose-li:my-0
        prose-pre:my-2 prose-pre:rounded-lg prose-pre:bg-zinc-950 prose-pre:p-3 prose-pre:text-xs
        prose-code:rounded prose-code:bg-zinc-200 dark:prose-code:bg-zinc-700 prose-code:px-1 prose-code:py-0.5 prose-code:text-[0.85em] prose-code:before:content-none prose-code:after:content-none
        prose-table:my-2 prose-table:text-xs prose-th:border prose-th:border-zinc-300 dark:prose-th:border-zinc-700 prose-th:px-2 prose-th:py-1 prose-td:border prose-td:border-zinc-300 dark:prose-td:border-zinc-700 prose-td:px-2 prose-td:py-1
        prose-blockquote:my-2 prose-blockquote:border-l-2 prose-blockquote:border-emerald-400 prose-blockquote:pl-3 prose-blockquote:italic
        prose-hr:my-3
      `
  ), [proseColor]);

  return (
    <div className={className}>
      <ReactMarkdown
        remarkPlugins={REMARK_PLUGINS}
        rehypePlugins={REHYPE_PLUGINS}
        components={MD_COMPONENTS}
      >
        {content || ""}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownMessage;
